import { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { clearDraft, clearEditMessage, clearReplyTo, setDraft, setEditMessage, setReplyTo } from './reducer';
const EMPTY_OBJ = {};

/**
 * Hook for chat-level draft operations.
 *
 * NOTE: This hook is specifically for the draft slice data (text and active file IDs).
 * Media file operations (preview, captions, upload) should be handled by
 * useMediaFile at the ViewModel layer to avoid layer violations.
 *
 * @param {Object} params
 * @param {string} params.activeChatId - Active user/group ID
 * @returns {Object} Draft data and basic operations
 */
export const useDraft = ({ activeChatId }) => {
   const dispatch = useDispatch();
   const drafts = useSelector(state => state.draftData?.drafts || EMPTY_OBJ, shallowEqual);

   const currentDraft = drafts[activeChatId] || {
      message: '',
      fileIds: [],
      activeFileId: null,
      mediaType: null,
      fileMetadata: {},
   };

   /**
    * Save text message draft.
    */
   const saveDraft = useCallback(
      message => {
         if (!activeChatId) return;
         dispatch(setDraft({ jid: activeChatId, message }));
      },
      [dispatch, activeChatId],
   );

   /**
    * Set reply to message ID.
    */
   const updateReplyTo = useCallback(
      msgId => {
         if (activeChatId) {
            dispatch(setReplyTo({ jid: activeChatId, msgId }));
         }
      },
      [dispatch, activeChatId],
   );

   /**
    * Clear reply to message ID.
    */
   const removeReplyTo = useCallback(() => {
      if (activeChatId) {
         dispatch(clearReplyTo(activeChatId));
      }
   }, [dispatch, activeChatId]);

   /**
    * Clear draft for the current chat.
    */
   const removeDraft = useCallback(() => {
      if (activeChatId) {
         dispatch(clearDraft(activeChatId));
      }
   }, [dispatch, activeChatId]);

   /**
    * Set edit message ID.
    */
   const updateEditMessage = useCallback(
      msgId => {
         if (activeChatId) {
            dispatch(setEditMessage({ jid: activeChatId, msgId }));
         }
      },
      [dispatch, activeChatId],
   );

   /**
    * Clear edit message ID.
    */
   const removeEditMessage = useCallback(() => {
      if (activeChatId) {
         dispatch(clearEditMessage(activeChatId));
         dispatch(clearDraft(activeChatId));
      }
   }, [dispatch, activeChatId]);

   return {
      draft: currentDraft,
      saveDraft,
      removeDraft,
      setReplyTo: updateReplyTo,
      clearReplyTo: removeReplyTo,
      setEditMessageId: updateEditMessage,
      clearEditMessage: removeEditMessage,
      activeChatId,
   };
};
